import { RULE_ROOTS } from "components/ContractLanguageSection/constants";
import { RuleType } from "components/ContractLanguageSection/types";
import { getAPIUrl } from "config/api";
import { ToastContext } from "context/ToastContext";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  IAppliedRule,
  IAppliedRuleAttachment,
  useGetClaimAppliedRulesQuery,
} from "store/queries/claimAppliedRules";
import { authSelector } from "store/slices/auth";
import styled from "styled-components";

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  MPPR: "MPPR",
  CASE_RATE: "Case Rate",
  PACKAGE: "Package",
  EXPLODING_FEE: "Exploding Fee",
  INPATIENT: "Inpatient",
};

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "tif",
  "tiff",
]);

const isImageAttachment = (fileName: string): boolean => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  return !!ext && IMAGE_EXTENSIONS.has(ext);
};

const buildAttachmentUrl = (
  ruleType: RuleType,
  ruleId: string,
  attachmentId: string
): string => {
  const apiBase = getAPIUrl().replace(/\/+$/, "");
  return `${apiBase}/${RULE_ROOTS[ruleType]}/${ruleId}/attachments/${attachmentId}/download`;
};

const Container = styled.div`
  margin: 1rem 1rem 1.5rem;

  /* Make the entire Panel header (not just the toggle icon) clickable. */
  .p-panel .p-panel-header {
    cursor: pointer;
    user-select: none;
  }
`;

const Entry = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const EntryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const RuleName = styled.span`
  font-weight: 600;
  color: #212529;
`;

const LanguageBlock = styled.div`
  background: #f8f9fa;
  border-left: 3px solid #6366f1;
  padding: 0.625rem 0.875rem;
  margin: 0.5rem 0;
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.5;
  color: #343a40;
`;

const ImageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ImagePreview = styled.img`
  display: block;
  width: 100%;
  max-width: 720px;
  max-height: 400px;
  object-fit: contain;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  cursor: pointer;
`;

const ImageSkeleton = styled.div`
  width: 100%;
  max-width: 720px;
  height: 200px;
  background: #f1f3f5;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 13px;
`;

const ImageFallback = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 4px;
  color: #6c757d;
  font-size: 13px;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const formatFileSize = (bytes: number): string => {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

interface IRuleImagePreviewProps {
  rule: IAppliedRule;
  attachment: IAppliedRuleAttachment;
  token: string | null;
}

function RuleImagePreview({ rule, attachment, token }: IRuleImagePreviewProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    const load = async () => {
      try {
        const url = buildAttachmentUrl(
          rule.ruleType,
          rule.ruleId,
          attachment.id
        );
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Inline image load failed", err);
        if (!cancelled) setErrored(true);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [rule.ruleType, rule.ruleId, attachment.id, token]);

  if (errored) {
    return (
      <ImageFallback>
        <i className="pi pi-image" />
        <span>{attachment.fileName} (failed to load)</span>
      </ImageFallback>
    );
  }
  if (!src) {
    return (
      <ImageSkeleton>
        <i className="pi pi-spin pi-spinner mr-2" />
        Loading {attachment.fileName}…
      </ImageSkeleton>
    );
  }
  return (
    <ImagePreview
      src={src}
      alt={attachment.fileName}
      onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
    />
  );
}

const renderHeader = (options: {
  className: string;
  titleClassName: string;
  collapsed: boolean;
  onTogglerClick: (e: React.MouseEvent<HTMLElement>) => void;
  togglerElement: React.ReactNode;
}) => (
  <div
    className={options.className}
    onClick={options.onTogglerClick}
    role="button"
    tabIndex={0}
    aria-expanded={!options.collapsed}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        options.onTogglerClick(e as unknown as React.MouseEvent<HTMLElement>);
      }
    }}
  >
    <span className={options.titleClassName}>Relevant Contract Language</span>
    <span
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {options.togglerElement}
    </span>
  </div>
);

interface IRelevantContractLanguageAccordionProps {
  claimId: string;
}

export function RelevantContractLanguageAccordion({
  claimId,
}: IRelevantContractLanguageAccordionProps) {
  const { token } = useSelector(authSelector);
  const { current: toastElement } = useContext(ToastContext);

  // Eager fetch — we need to know up front whether there's anything to show
  // so we can hide the panel entirely when there isn't (instead of rendering
  // a header that disappears once the user expands it).
  const { data, isLoading, isError } = useGetClaimAppliedRulesQuery(claimId);

  const rules = data?.data ?? [];
  const hasContent = rules.some(
    (r) => !!r.contractLanguage || r.attachments.length > 0
  );

  // Hide entirely while loading, on error, or when there's nothing to show.
  if (isLoading || isError || !hasContent) {
    return null;
  }

  const downloadAttachment = async (
    rule: IAppliedRule,
    attachment: IAppliedRuleAttachment
  ) => {
    try {
      const url = buildAttachmentUrl(rule.ruleType, rule.ruleId, attachment.id);
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Attachment download failed", err);
      toastElement?.show({
        severity: "error",
        summary: "Download failed",
        detail:
          err instanceof Error ? err.message : "Failed to download attachment.",
      });
    }
  };

  return (
    <Container>
      <Panel toggleable collapsed headerTemplate={renderHeader}>
        {rules.map((rule) => {
          if (!rule.contractLanguage && rule.attachments.length === 0) {
            return null;
          }
          const images = rule.attachments.filter((a) =>
            isImageAttachment(a.fileName)
          );
          const nonImages = rule.attachments.filter(
            (a) => !isImageAttachment(a.fileName)
          );
          return (
            <Entry key={`${rule.ruleType}-${rule.ruleId}`}>
              <EntryHeader>
                <RuleName>{rule.ruleName}</RuleName>
                <Badge
                  value={RULE_TYPE_LABELS[rule.ruleType]}
                  severity="info"
                />
              </EntryHeader>
              {rule.contractLanguage && (
                <LanguageBlock>{rule.contractLanguage}</LanguageBlock>
              )}
              {images.length > 0 && (
                <ImageStack>
                  {images.map((attachment) => (
                    <RuleImagePreview
                      key={attachment.id}
                      rule={rule}
                      attachment={attachment}
                      token={token}
                    />
                  ))}
                </ImageStack>
              )}
              {nonImages.length > 0 && (
                <AttachmentList>
                  {nonImages.map((attachment) => (
                    <Button
                      key={attachment.id}
                      icon="pi pi-paperclip"
                      label={`${attachment.fileName} (${formatFileSize(
                        attachment.fileSize
                      )})`}
                      className="p-button-outlined p-button-sm"
                      onClick={() => downloadAttachment(rule, attachment)}
                    />
                  ))}
                </AttachmentList>
              )}
            </Entry>
          );
        })}
      </Panel>
    </Container>
  );
}
